import React, { useState, useEffect, useContext } from "react";
import secureAxios from "../../utils/secureAxios";
import { getApiBaseURL } from "../../utils/api";
import { AuthContext } from "../../contexts/AuthContext";
import { formatPhone } from "../../utils/formatPhone";

const CreateTenantForm = ({ onCreated }) => {
  const [formData, setFormData] = useState({
    tenant_name: "",
    tenant_type: "customer",
    email: "", // Added email field for notifications
    phone_number: "",
    address_line_1: "",
    address_line_2: "",
    city: "",
    state_id: "",
    postal_code: "",
    is_active: true,
    emergency_contact_name: "", // Added for HIPAA emergency access
    emergency_contact_phone: "", // Added for HIPAA emergency access
  });

  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const apiBase = getApiBaseURL();
  const { user } = useContext(AuthContext);

  useEffect(() => {
    fetchStates();
  }, []);

  const fetchStates = async () => {
    try {
      const response = await secureAxios.get(`${apiBase}/api/states`);
      setStates(response.data);
    } catch (err) {
      console.error("Error loading states", err);
      setError("Failed to load states. Please refresh the page.");
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    let newValue = value;
    if (name === "phone_number" || name === "emergency_contact_phone") {
      newValue = formatPhone(value);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : newValue,
    }));

    if (error) setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Validate required fields
      if (!formData.tenant_name.trim()) {
        setError("Tenant name is required");
        setLoading(false);
        return;
      }

      if (!formData.email.trim()) {
        setError("Email is required for HIPAA notifications");
        setLoading(false);
        return;
      }

      // Clean phone numbers for storage
      const cleanedPhone = formData.phone_number.replace(/\D/g, "");
      const cleanedEmergencyPhone = formData.emergency_contact_phone.replace(
        /\D/g,
        ""
      );

      const cleanedData = {
        ...formData,
        state_id: formData.state_id === "" ? null : formData.state_id,
        phone_number: cleanedPhone,
        emergency_contact_phone: cleanedEmergencyPhone,
        tenant_name: formData.tenant_name.trim(),
        email: formData.email.trim().toLowerCase(),
        address_line_1: formData.address_line_1.trim(),
        address_line_2: formData.address_line_2.trim(),
        city: formData.city.trim(),
        postal_code: formData.postal_code.trim(),
        emergency_contact_name: formData.emergency_contact_name.trim(),
        created_by: user.user_id, // Track who created the tenant
      };

      await secureAxios.post(`${apiBase}/api/tenants`, cleanedData);

      alert("✅ Tenant created successfully!");

      // Reset form
      setFormData({
        tenant_name: "",
        tenant_type: "customer",
        email: "",
        phone_number: "",
        address_line_1: "",
        address_line_2: "",
        city: "",
        state_id: "",
        postal_code: "",
        is_active: true,
        emergency_contact_name: "",
        emergency_contact_phone: "",
      });

      if (onCreated) onCreated();
    } catch (err) {
      console.error("❌ Failed to create tenant:", err);
      setError(err.response?.data?.error || "Failed to create tenant");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {error && (
        <div className="alert alert-danger alert-dismissible" role="alert">
          {error}
          <button
            type="button"
            className="btn-close"
            onClick={() => setError(null)}
            aria-label="Close"
          ></button>
        </div>
      )}

      <form onSubmit={handleSubmit} className="row g-3">
        {/* Tenant Name */}
        <div className="col-md-6">
          <label className="form-label">
            Tenant Name <span className="text-danger">*</span>
          </label>
          <input
            className="form-control"
            name="tenant_name"
            value={formData.tenant_name}
            onChange={handleChange}
            placeholder="Enter tenant name"
            required
            disabled={loading}
          />
        </div>

        {/* Tenant Type */}
        <div className="col-md-6">
          <label className="form-label">Type</label>
          <select
            className="form-select"
            name="tenant_type"
            value={formData.tenant_type}
            onChange={handleChange}
            disabled={loading}
          >
            <option value="customer">Customer</option>
            <option value="vendor">Vendor</option>
            <option value="partner">Partner</option>
          </select>
          {formData.tenant_type === "customer" && (
            <small className="text-info">
              <i className="bi bi-info-circle"></i> A customer record will be
              created automatically
            </small>
          )}
        </div>

        {/* Email */}
        <div className="col-md-6">
          <label className="form-label">
            Email <span className="text-danger">*</span>
            <small className="text-muted ms-2">
              Required for HIPAA notifications
            </small>
          </label>
          <input
            className="form-control"
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="admin@tenant.com"
            required
            disabled={loading}
          />
        </div>

        {/* Phone Number */}
        <div className="col-md-6">
          <label className="form-label">Phone Number</label>
          <input
            className="form-control"
            type="tel"
            name="phone_number"
            value={formData.phone_number}
            onChange={handleChange}
            placeholder="(123) 456-7890"
            disabled={loading}
          />
        </div>

        {/* Address Section */}
        <div className="col-12">
          <h5 className="border-bottom pb-2">Address Information</h5>
        </div>

        {/* Address Line 1 */}
        <div className="col-12">
          <label className="form-label">Address Line 1</label>
          <input
            className="form-control"
            name="address_line_1"
            value={formData.address_line_1}
            onChange={handleChange}
            placeholder="Street address"
            disabled={loading}
          />
        </div>

        {/* Address Line 2 */}
        <div className="col-12">
          <label className="form-label">Address Line 2</label>
          <input
            className="form-control"
            name="address_line_2"
            value={formData.address_line_2}
            onChange={handleChange}
            placeholder="Apartment, suite, etc."
            disabled={loading}
          />
        </div>

        {/* City */}
        <div className="col-md-4">
          <label className="form-label">City</label>
          <input
            className="form-control"
            name="city"
            value={formData.city}
            onChange={handleChange}
            placeholder="City"
            disabled={loading}
          />
        </div>

        {/* State */}
        <div className="col-md-4">
          <label className="form-label">State</label>
          <select
            className="form-select"
            name="state_id"
            value={formData.state_id}
            onChange={handleChange}
            disabled={loading}
          >
            <option value="">Select State</option>
            {states.map((state) => (
              <option key={state.state_id} value={state.state_id}>
                {state.state_code} - {state.state_name}
              </option>
            ))}
          </select>
        </div>

        {/* Postal Code */}
        <div className="col-md-4">
          <label className="form-label">ZIP Code</label>
          <input
            className="form-control"
            name="postal_code"
            value={formData.postal_code}
            onChange={handleChange}
            placeholder="12345"
            maxLength="10"
            disabled={loading}
          />
        </div>

        {/* Emergency Contact Section */}
        <div className="col-12">
          <h5 className="border-bottom pb-2">
            Emergency Contact
            <small className="text-muted ms-2">
              For HIPAA break-glass scenarios
            </small>
          </h5>
        </div>

        {/* Emergency Contact Name */}
        <div className="col-md-6">
          <label className="form-label">Emergency Contact Name</label>
          <input
            className="form-control"
            name="emergency_contact_name"
            value={formData.emergency_contact_name}
            onChange={handleChange}
            placeholder="Name of emergency contact"
            disabled={loading}
          />
        </div>

        {/* Emergency Contact Phone */}
        <div className="col-md-6">
          <label className="form-label">Emergency Contact Phone</label>
          <input
            className="form-control"
            type="tel"
            name="emergency_contact_phone"
            value={formData.emergency_contact_phone}
            onChange={handleChange}
            placeholder="(123) 456-7890"
            disabled={loading}
          />
        </div>

        {/* Create as Customer Option */}
        {formData.tenant_type !== "customer" && (
          <div className="col-12">
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                name="create_as_customer"
                checked={formData.create_as_customer || false}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    create_as_customer: e.target.checked,
                  }))
                }
                id="create_as_customer"
                disabled={loading}
              />
              <label className="form-check-label" htmlFor="create_as_customer">
                <strong>Also create as customer</strong>
                <br />
                <small className="text-muted">
                  This allows other tenants to link to this tenant as a customer
                </small>
              </label>
            </div>
          </div>
        )}

        {/* Active Status */}
        <div className="col-12">
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              name="is_active"
              checked={formData.is_active}
              onChange={handleChange}
              id="is_active"
              disabled={loading}
            />
            <label className="form-check-label" htmlFor="is_active">
              <strong>Active</strong> - Tenant has system access
              <br />
              <small className="text-muted">
                Inactive tenants cannot log in but their data is preserved
              </small>
            </label>
          </div>
        </div>

        {/* Submit Buttons */}
        <div className="col-12">
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                Creating...
              </>
            ) : (
              <>
                <i className="bi bi-plus-circle"></i> Create Tenant
              </>
            )}
          </button>
          <button
            type="button"
            className="btn btn-secondary ms-2"
            onClick={() => {
              setFormData({
                tenant_name: "",
                tenant_type: "customer",
                email: "",
                phone_number: "",
                address_line_1: "",
                address_line_2: "",
                city: "",
                state_id: "",
                postal_code: "",
                is_active: true,
                emergency_contact_name: "",
                emergency_contact_phone: "",
              });
              setError(null);
            }}
            disabled={loading}
          >
            Clear
          </button>
        </div>
      </form>

      {/* PHI Notice */}
      <div className="alert alert-info mt-3" role="alert">
        <i className="bi bi-shield-lock"></i> <strong>Privacy Notice:</strong>
        <ul className="mb-0 mt-2">
          <li>
            Tenant information may contain Protected Health Information (PHI)
          </li>
          <li>All data is encrypted at rest and in transit (AES-256)</li>
          <li>Access is logged for HIPAA compliance with 7-year retention</li>
          <li>Only primary tenant administrators can create new tenants</li>
          <li>Email is required for HIPAA breach notifications</li>
          <li>Emergency contacts are for break-glass access scenarios</li>
        </ul>
        {formData.tenant_type === "customer" && (
          <div className="mt-2">
            <i className="bi bi-info-circle"></i> <strong>Note:</strong>{" "}
            Customer-type tenants will automatically have a customer record
            created, making them available for linking by other tenants.
          </div>
        )}
      </div>

      {/* Authentication Notice */}
      <div className="alert alert-warning mt-2" role="alert">
        <i className="bi bi-key"></i> <strong>Authentication Setup:</strong>
        <br />
        Tenant authentication credentials will be set up separately after
        creation. The tenant admin will receive an email with instructions to
        set their password.
      </div>
    </div>
  );
};

export default CreateTenantForm;
